// GateUserSelection.tsx - Multi-select gate user component for approvers/reviewers
import React, { useState, useMemo, useRef } from 'react';
import './GateManagement.css';
import { GateUser } from './types';

interface GateUserSelectionProps {
  users: GateUser[];
  selectedUserIds: number[];
  onSelectionChange: (selectedIds: number[]) => void;
  label: string;
  placeholder?: string;
  isLoading?: boolean;
  disabled?: boolean;
}

export const GateUserSelection: React.FC<GateUserSelectionProps> = ({
  users,
  selectedUserIds,
  onSelectionChange,
  label,
  placeholder = "Search users by name or email...",
  isLoading = false,
  disabled = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Debug logging
  console.log('🎨 GateUserSelection render:', {
    label,
    usersCount: users.length,
    selectedCount: selectedUserIds.length,
    isLoading,
    disabled
  });

  // Filter users based on search term
  const filteredUsers = useMemo(() => {
    if (!searchTerm.trim()) {
      return users;
    }

    const term = searchTerm.toLowerCase().trim();
    
    return users.filter(user => 
      user.userName.toLowerCase().includes(term) ||
      user.userEmail.toLowerCase().includes(term) ||
      (user.role && user.role.toLowerCase().includes(term))
    );
  }, [users, searchTerm]);

  // Get selected users details
  const selectedUsers = useMemo(() => {
    return users.filter(user => selectedUserIds.includes(user.userId));
  }, [users, selectedUserIds]);

  // Handle user selection/deselection
  const handleUserToggle = (userId: number) => {
    console.log('🔍 GateUserSelection - handleUserToggle called');
    console.log('  - userId:', userId, 'type:', typeof userId);
    console.log('  - selectedUserIds:', selectedUserIds);
    console.log('  - users length:', users.length);
    
    if (selectedUserIds.includes(userId)) {
      // Remove user
      console.log('  ➖ Removing user');
      const newSelection = selectedUserIds.filter(id => id !== userId);
      console.log('  - New selection:', newSelection);
      onSelectionChange(newSelection);
    } else {
      // Add user
      console.log('  ➕ Adding user');
      const newSelection = [...selectedUserIds, userId];
      console.log('  - New selection:', newSelection);
      onSelectionChange(newSelection);
    }

    setSearchTerm('');
    setIsDropdownOpen(false);
    searchInputRef.current?.blur();
  };

  // Remove user from selection
  const handleRemoveUser = (userId: number) => {
    onSelectionChange(selectedUserIds.filter(id => id !== userId));
  };

  // Clear all selections
  const handleClearAll = () => {
    onSelectionChange([]);
  };

  if (isLoading) {
    return (
      <div className="form-group">
        <label className="form-label">{label}</label>
        <div className="user-selection-loading">
          <span className="spinner"></span>
          Loading users...
        </div>
      </div>
    );
  }

  return (
    <div className="form-group">
      <label className="form-label">
        {label}
        {selectedUsers.length > 0 && (
          <span className="selection-count"> ({selectedUsers.length} selected)</span>
        )}
      </label>

      {/* Selected Users Display */}
      {selectedUsers.length > 0 && (
        <div className="selected-users">
          <div className="selected-users-header">
            <span>Selected {label.toLowerCase()}:</span>
            <button
              type="button"
              onClick={handleClearAll}
              className="clear-all-btn"
              disabled={disabled}
            >
              Clear All
            </button>
          </div>
          <div className="selected-users-list">
            {selectedUsers.map(user => (
              <div key={user.userId} className="selected-user-tag">
                <div className="user-info">
                  <strong>{user.userName}</strong>
                  <small>{user.userEmail}</small>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveUser(user.userId)}
                  className="remove-user-btn"
                  title="Remove user"
                  disabled={disabled}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* User Search and Selection */}
      <div className="user-selection-dropdown">
        <div className="search-input-container">
          <input
            ref={searchInputRef}
            type="text"
            placeholder={placeholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => setIsDropdownOpen(true)}
            className="form-input user-search-input"
            disabled={disabled}
          />
          <span className="search-icon">🔍</span>
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm('')}
              className="clear-search-btn"
              title="Clear search"
            >
              ✕
            </button>
          )}
        </div>

        {isDropdownOpen && (
          <div className="user-dropdown">
            <div className="dropdown-header">
              <span>Available Users ({filteredUsers.length})</span>
              <button
                type="button"
                onClick={() => setIsDropdownOpen(false)}
                className="close-dropdown-btn"
              >
                ✕
              </button>
            </div>
            
            <div className="user-list">
              {filteredUsers.length === 0 ? (
                <div className="no-users-found">
                  {searchTerm ? `No users found matching "${searchTerm}"` : 'No users available'}
                </div>
              ) : (
                filteredUsers.map(user => {
                  const isSelected = selectedUserIds.includes(user.userId);
                  return (
                    <div
                      key={user.userId}
                      className={`user-option ${isSelected ? 'selected' : ''}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log('🖱️ User clicked:', user.userId, user.userName);
                        if (!disabled) {
                          handleUserToggle(user.userId);
                        } else {
                          console.log('⚠️ Click ignored - component is disabled');
                        }
                      }}
                      style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
                    >
                      <div className="user-checkbox">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}} // Controlled by parent onClick
                          disabled={disabled}
                        />
                      </div>
                      <div className="user-details">
                        <div className="user-name">{user.userName}</div>
                        <div className="user-email">{user.userEmail}</div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {/* Click outside to close dropdown */}
      {isDropdownOpen && (
        <div 
          className="dropdown-overlay" 
          onClick={() => setIsDropdownOpen(false)}
        />
      )}

      <small className="form-help">
        Search and select multiple users by clicking on them.
      </small>
    </div>
  );
};